import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminChrome from '@/components/AdminChrome';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, DollarSign, Briefcase, Shield, History, KeyRound, Copy } from 'lucide-react';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';

const EMPTY = {
  name: '', phone: '', address: '',
  emergency_contact_name: '', emergency_contact_phone: '', date_of_birth: '',
  employment_type: 'intern', pay_rate: '', pay_rate_unit: 'hourly', pay_currency: 'USD',
  start_date: '', end_date: '', manager_id: '', department: '',
  intern_role: '', intern_role_label: '', is_staff: false, is_active: true,
  permissions: [], team_role: 'viewer', notes: '',
};

const HREmployeeEditPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, new_email: '', notify: true });
  const [changingEmail, setChangingEmail] = useState(false);
  const [pwDialog, setPwDialog] = useState({ open: false, mode: 'generate', new_password: '', notify: true });
  const [resettingPw, setResettingPw] = useState(false);
  const [pwResult, setPwResult] = useState(null);

  const resetPassword = async () => {
    if (pwDialog.mode === 'set' && !isPasswordValid(pwDialog.new_password || '')) {
      toast.error('Password must include upper, lower, number, and special character (min 8).');
      return;
    }
    setResettingPw(true);
    try {
      const body = { notify_employee: pwDialog.notify };
      if (pwDialog.mode === 'set') body.new_password = pwDialog.new_password;
      const res = await api.post(`/admin/employees/${employeeId}/reset-password`, body);
      if (res.data?.generated && res.data?.new_password) {
        setPwResult(res.data.new_password);
        toast.success('Password reset — copy it before closing.');
      } else {
        toast.success('Password reset');
        setPwDialog({ open: false, mode: 'generate', new_password: '', notify: true });
        setPwResult(null);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPw(false);
    }
  };

  const closePwDialog = () => {
    setPwDialog({ open: false, mode: 'generate', new_password: '', notify: true });
    setPwResult(null);
  };

  const changeEmail = async () => {
    if (!emailDialog.new_email || !emailDialog.new_email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setChangingEmail(true);
    try {
      await api.patch(`/admin/employees/${employeeId}/email`, {
        new_email: emailDialog.new_email.trim(),
        notify_employee: emailDialog.notify,
      });
      toast.success(`Login email updated → ${emailDialog.new_email}`);
      setEmailDialog({ open: false, new_email: '', notify: true });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setChangingEmail(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        api.get(`/admin/employees/${employeeId}/hr-profile`),
        api.get(`/admin/employees/${employeeId}/hr-audit`).catch(() => ({ data: { audit: [] } })),
      ]);
      setProfile(pRes.data);
      setForm({
        ...EMPTY,
        ...pRes.data,
        permissions: pRes.data.permissions || [],
      });
      setAudit(aRes.data?.audit || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load employee');
      navigate('/admin/team');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields the admin actually changed / has values
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === '' || v === null || v === undefined) continue;
        payload[k] = k === 'pay_rate' ? Number(v) : v;
      }
      await api.patch(`/admin/employees/${employeeId}/hr-profile`, payload);
      toast.success('Profile saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading || !profile) {
    return (
      <AdminChrome activeSection="team">
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </AdminChrome>
    );
  }

  const displayName = profile.name || profile.email;
  const source = profile.source === 'platform_employees' ? 'Platform employee' : 'User account';

  return (
    <AdminChrome activeSection="team">
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/team')} data-testid="hr-edit-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Team
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 mt-2" data-testid="hr-edit-title">Edit HR profile — {displayName}</h1>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{source}</Badge>
              <span>{profile.email}</span>
              {profile.intern_role_label && <Badge className="bg-emerald-100 text-emerald-700">{profile.intern_role_label}</Badge>}
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="hr-edit-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>

        <Tabs defaultValue="personal">
          <TabsList>
            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
            <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="tab-permissions">Permissions</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-3 h-3 mr-1" />Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <Card className="mt-4"><CardHeader><CardTitle className="text-lg">Contact info</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" value={form.name} onChange={(v) => setField('name', v)} testid="hr-name" />
                <div className="md:col-span-2">
                  <Label>Login email</Label>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Input value={profile.email || ''} disabled className="bg-slate-50 min-w-[240px] flex-1" data-testid="hr-current-email" />
                    <Button
                      variant="outline"
                      onClick={() => setEmailDialog({ open: true, new_email: '', notify: true })}
                      data-testid="hr-change-email-btn"
                    >
                      Change email
                    </Button>
                    <Button
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => setPwDialog({ open: true, mode: 'generate', new_password: '', notify: true })}
                      data-testid="hr-reset-password-btn"
                    >
                      <KeyRound className="w-4 h-4 mr-1" /> Reset password
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">The employee will log in with this address. Change it here (e.g. personal → work). Password reset generates a new one you can share securely, or set your own.</p>
                </div>
                <Field label="Phone" value={form.phone} onChange={(v) => setField('phone', v)} testid="hr-phone" />
                <Field label="Date of birth" value={form.date_of_birth} onChange={(v) => setField('date_of_birth', v)} testid="hr-dob" placeholder="YYYY-MM-DD" />
                <Field label="Address" value={form.address} onChange={(v) => setField('address', v)} testid="hr-address" />
                <Field label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setField('emergency_contact_name', v)} testid="hr-ec-name" />
                <Field label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setField('emergency_contact_phone', v)} testid="hr-ec-phone" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="employment">
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Briefcase className="w-4 h-4" /> Employment</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Employment type</Label>
                  <select className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1" value={form.employment_type} onChange={(e) => setField('employment_type', e.target.value)} data-testid="hr-emp-type">
                    <option value="intern">Intern (unpaid)</option>
                    <option value="paid_intern">Paid intern</option>
                    <option value="part_time">Part-time</option>
                    <option value="full_time">Full-time</option>
                    <option value="contractor">Contractor</option>
                  </select>
                </div>
                <Field label="Department" value={form.department} onChange={(v) => setField('department', v)} testid="hr-department" />
                <Field label="Start date" value={form.start_date} onChange={(v) => setField('start_date', v)} testid="hr-start-date" placeholder="YYYY-MM-DD or 'TBD'" />
                <Field label="End date (if applicable)" value={form.end_date} onChange={(v) => setField('end_date', v)} testid="hr-end-date" placeholder="YYYY-MM-DD" />
                <Field label="Manager (user id)" value={form.manager_id} onChange={(v) => setField('manager_id', v)} testid="hr-manager" />
                <Field label="Role label" value={form.intern_role_label} onChange={(v) => setField('intern_role_label', v)} testid="hr-role-label" />
                <div className="md:col-span-2 border-t pt-3 mt-1">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-1 mb-2"><DollarSign className="w-4 h-4 text-emerald-500" /> Compensation</p>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Pay rate" value={form.pay_rate} onChange={(v) => setField('pay_rate', v)} testid="hr-pay-rate" placeholder="18.50" />
                    <div>
                      <Label>Unit</Label>
                      <select className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1" value={form.pay_rate_unit} onChange={(e) => setField('pay_rate_unit', e.target.value)} data-testid="hr-pay-unit">
                        <option value="hourly">Hourly</option>
                        <option value="annual">Annual salary</option>
                        <option value="stipend">Stipend</option>
                      </select>
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <select className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1" value={form.pay_currency} onChange={(e) => setField('pay_currency', e.target.value)} data-testid="hr-pay-currency">
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>HR notes (private — admins only)</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="hr-notes" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissions">
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="w-4 h-4" /> Access &amp; Permissions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Field label="Intern role key" value={form.intern_role} onChange={(v) => setField('intern_role', v)} testid="hr-role-key" placeholder="social_media, marketing, content_curriculum, …" />
                <div>
                  <Label>Team role</Label>
                  <select className="w-full border border-slate-300 rounded-md px-3 py-2 mt-1" value={form.team_role} onChange={(e) => setField('team_role', e.target.value)} data-testid="hr-team-role">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <Label>Permissions (comma-separated)</Label>
                  <Textarea
                    rows={3}
                    value={(form.permissions || []).join(', ')}
                    onChange={(e) => setField('permissions', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                    data-testid="hr-permissions"
                    placeholder="social.compose, social.publish, content.create_lesson, …"
                  />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.is_staff} onChange={(e) => setField('is_staff', e.target.checked)} data-testid="hr-is-staff" /> Staff account
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setField('is_active', e.target.checked)} data-testid="hr-is-active" /> Active
                  </label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="mt-4">
              <CardHeader><CardTitle className="text-lg">Change history</CardTitle></CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">No edits recorded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {audit.map((a) => (
                      <div key={a.id} className="border border-slate-200 rounded p-3 text-sm">
                        <p className="text-xs text-slate-500">{new Date(a.updated_at).toLocaleString()} · <strong>{a.updated_by}</strong></p>
                        <pre className="mt-1 text-xs bg-slate-50 p-2 rounded overflow-x-auto">{JSON.stringify(a.changes, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Change login email dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => setEmailDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle>Change login email</DialogTitle>
            <DialogDescription>
              Update <strong>{profile?.email}</strong> to a new address. The employee will log in with this new email immediately — password stays the same. Both addresses are notified by default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New email (work address recommended)</Label>
              <Input
                type="email"
                value={emailDialog.new_email}
                onChange={(e) => setEmailDialog((d) => ({ ...d, new_email: e.target.value }))}
                placeholder="jamie@zazued.com"
                data-testid="new-email-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={emailDialog.notify}
                onChange={(e) => setEmailDialog((d) => ({ ...d, notify: e.target.checked }))}
                data-testid="notify-employee-checkbox"
              />
              Notify the employee at both the old and new address
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, new_email: '', notify: true })}>Cancel</Button>
            <Button onClick={changeEmail} disabled={changingEmail} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-change-email-btn">
              {changingEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Update email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwDialog.open} onOpenChange={(o) => (o ? null : closePwDialog())}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Reset employee password</DialogTitle>
            <DialogDescription>
              Reset the login password for <strong>{profile?.email}</strong>. Choose to auto-generate a strong one, or type your own. The employee&apos;s session will remain valid until they log out.
            </DialogDescription>
          </DialogHeader>
          {pwResult ? (
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-sm font-semibold text-emerald-800 mb-2">New password generated</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white border border-emerald-300 rounded font-mono text-lg tracking-wide select-all" data-testid="generated-password-value">{pwResult}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(pwResult); toast.success('Copied to clipboard'); }}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-emerald-700 mt-2">Share via a secure channel. This is the only time it will be shown.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Button
                  variant={pwDialog.mode === 'generate' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPwDialog((d) => ({ ...d, mode: 'generate', new_password: '' }))}
                  data-testid="pw-mode-generate"
                >
                  Auto-generate
                </Button>
                <Button
                  variant={pwDialog.mode === 'set' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPwDialog((d) => ({ ...d, mode: 'set' }))}
                  data-testid="pw-mode-set"
                >
                  Set my own
                </Button>
              </div>
              {pwDialog.mode === 'set' && (
                <div>
                  <Label>New password</Label>
                  <Input
                    type="text"
                    value={pwDialog.new_password}
                    onChange={(e) => setPwDialog((d) => ({ ...d, new_password: e.target.value }))}
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <div className="mt-2">
                    <PasswordStrengthMeter password={pwDialog.new_password} />
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pwDialog.notify}
                  onChange={(e) => setPwDialog((d) => ({ ...d, notify: e.target.checked }))}
                  data-testid="notify-password-checkbox"
                />
                Email the employee with the new password
              </label>
            </div>
          )}
          <DialogFooter>
            {pwResult ? (
              <Button onClick={closePwDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="close-pw-dialog-btn">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closePwDialog}>Cancel</Button>
                <Button onClick={resetPassword} disabled={resettingPw} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-reset-pw-btn">
                  {resettingPw ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
                  Reset password
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminChrome>
  );
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <Label>{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
  </div>
);

export default HREmployeeEditPage;
